<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div>
        <h6 class="mb-0">{{ itemsLabel }}</h6>
        <small class="text-muted">{{ itemsHint }}</small>
      </div>
      <button v-if="!form" type="button" class="btn btn-sm btn-primary" @click="startAdd('main')">
        <i class="bi bi-plus-lg me-1"></i>Add {{ singular }}
      </button>
    </div>

    <!-- Item form -->
    <div v-if="form" class="card border-primary mb-3">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-5" v-if="!isFeatures">
            <label class="form-label small fw-bold mb-1">Image</label>
            <div class="item-image-preview rounded border mb-2" :style="{ aspectRatio: String(cropPreset.aspectRatio) }">
              <img v-if="form.image" :src="form.image" alt="">
              <div v-else class="text-muted small text-center p-2">
                <i class="bi bi-image fs-3 d-block"></i>No image yet
              </div>
            </div>
            <input ref="fileInput" type="file" accept=".jpg,.jpeg,.png,.webp" hidden @change="onFileSelected">
            <button type="button" class="btn btn-sm btn-outline-primary w-100" @click="$refs.fileInput.click()">
              <i class="bi bi-upload me-1"></i>{{ form.image ? 'Replace' : 'Upload' }} &amp; crop image
            </button>
            <small class="text-muted d-block mt-1">Cropped to {{ cropPreset.label }}</small>
          </div>
          <div :class="isFeatures ? 'col-12' : 'col-md-7'">
            <div class="row g-2">
              <div class="col-12" v-if="section.type === 'hero_carousel'">
                <label class="form-label small fw-bold mb-1">Placement</label>
                <div class="btn-group btn-group-sm w-100">
                  <button type="button" class="btn" :class="form.placement === 'main' ? 'btn-primary' : 'btn-outline-primary'" @click="form.placement = 'main'">
                    Main slide
                  </button>
                  <button type="button" class="btn" :class="form.placement === 'side' ? 'btn-primary' : 'btn-outline-primary'" @click="form.placement = 'side'">
                    Side banner
                  </button>
                </div>
              </div>
              <div class="col-12" v-if="isFeatures">
                <label class="form-label small fw-bold mb-1">Icon</label>
                <div class="d-flex flex-wrap gap-1">
                  <button v-for="icon in featureIcons" :key="icon" type="button" class="btn btn-sm icon-choice"
                          :class="form.icon === icon ? 'btn-primary' : 'btn-outline-secondary'" @click="form.icon = icon" :title="icon">
                    <i :class="'bi bi-' + icon"></i>
                  </button>
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label small fw-bold mb-1">Title</label>
                <input v-model="form.title" type="text" class="form-control form-control-sm" maxlength="120">
              </div>
              <div class="col-md-6">
                <label class="form-label small fw-bold mb-1">Subtitle</label>
                <input v-model="form.subtitle" type="text" class="form-control form-control-sm" maxlength="200">
              </div>
              <template v-if="!isFeatures">
                <div class="col-md-4">
                  <label class="form-label small fw-bold mb-1">Button text</label>
                  <input v-model="form.button_text" type="text" class="form-control form-control-sm" maxlength="60" placeholder="e.g. Shop now">
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-bold mb-1">Text position</label>
                  <select v-model="form.text_position" class="form-select form-select-sm">
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-bold mb-1">Text color</label>
                  <div class="d-flex gap-1">
                    <input v-model="form.text_color" type="color" class="form-control form-control-sm form-control-color">
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="form.text_color = ''" title="Use default">
                      <i class="bi bi-eraser"></i>
                    </button>
                  </div>
                </div>
                <div class="col-12">
                  <label class="form-label small fw-bold mb-1">When clicked, go to</label>
                  <LinkEditor v-model:type="form.link_type" v-model:value="form.link_value" :categories="categories" :brands="brands" />
                </div>
              </template>
              <div class="col-12">
                <div class="form-check form-switch">
                  <input :id="'item-active-' + section.id" v-model="form.is_active" type="checkbox" class="form-check-input">
                  <label :for="'item-active-' + section.id" class="form-check-label small">Visible in the shop</label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="form = null">Cancel</button>
          <button type="button" class="btn btn-sm btn-primary" :disabled="saving || !canSave" @click="save">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            {{ form.id ? 'Save' : 'Add' }} {{ singular }}
          </button>
        </div>
      </div>
    </div>

    <!-- Items list -->
    <div v-if="!items.length && !form" class="text-center text-muted border rounded py-4 small">
      <i class="bi bi-inbox fs-3 d-block"></i>
      Nothing added yet.
      <span v-if="isFeatures">Default selling points will be shown until you add your own.</span>
      <span v-else-if="section.type === 'hero_carousel'">Your store's background image is shown until you add slides.</span>
      <span v-else>This section stays hidden in the shop until it has at least one image.</span>
    </div>

    <template v-for="group in groups" :key="group.placement">
      <p v-if="groups.length > 1" class="small fw-bold text-muted mb-1 mt-2">{{ group.label }}</p>
      <div class="list-group mb-2">
        <div v-for="(item, index) in group.items" :key="item.id" class="list-group-item d-flex align-items-center gap-2"
             :class="{ 'opacity-50': !item.is_active }">
          <div class="d-flex flex-column">
            <button type="button" class="btn btn-sm btn-link p-0 text-secondary" :disabled="index === 0" @click="move(group, index, -1)">
              <i class="bi bi-chevron-up"></i>
            </button>
            <button type="button" class="btn btn-sm btn-link p-0 text-secondary" :disabled="index === group.items.length - 1" @click="move(group, index, 1)">
              <i class="bi bi-chevron-down"></i>
            </button>
          </div>
          <div v-if="isFeatures" class="feature-icon rounded-circle bg-light d-flex align-items-center justify-content-center">
            <i :class="'bi bi-' + (item.icon || 'star')"></i>
          </div>
          <img v-else :src="item.image" alt="" class="item-thumb rounded border">
          <div class="flex-grow-1 min-w-0">
            <div class="fw-semibold text-truncate">{{ item.title || '(no title)' }}</div>
            <div class="small text-muted text-truncate">
              {{ item.subtitle }}
              <span v-if="item.link_type && item.link_type !== 'none'" class="badge text-bg-light ms-1">
                <i class="bi bi-link-45deg"></i> {{ linkTypes[item.link_type] }}
              </span>
            </div>
          </div>
          <div class="form-check form-switch m-0" title="Visible">
            <input type="checkbox" class="form-check-input" :checked="item.is_active" @change="toggle(item)">
          </div>
          <button type="button" class="btn btn-sm btn-outline-primary" @click="startEdit(item)"><i class="bi bi-pencil"></i></button>
          <button type="button" class="btn btn-sm btn-outline-danger" @click="remove(item)"><i class="bi bi-trash"></i></button>
        </div>
      </div>
      <button v-if="section.type === 'hero_carousel' && group.placement === 'side' && !form" type="button"
              class="btn btn-sm btn-outline-primary mb-2" @click="startAdd('side')">
        <i class="bi bi-plus-lg me-1"></i>Add side banner
      </button>
    </template>

    <ImageCropper
      :open="cropper.open"
      :file="cropper.file"
      :aspect-ratio="cropPreset.aspectRatio"
      :output-width="cropPreset.width"
      :output-height="cropPreset.height"
      :title="'Crop ' + singular + ' image'"
      @cropped="dataUrl => form && (form.image = dataUrl)"
      @closed="cropper.open = false"
    />
  </div>
</template>

<script>
import storefrontService from "@/api/storefrontService";
import confirmDialog from "@/utils/confirmDialog";
import LinkEditor from "./LinkEditor.vue";
import { FEATURE_ICONS, ITEM_CROP, LINK_TYPES } from "../storefrontMeta";

const emptyItem = (placement = 'main') => ({
  id: null,
  placement,
  image: '',
  title: '',
  subtitle: '',
  button_text: '',
  link_type: 'none',
  link_value: '',
  text_color: '',
  text_position: 'left',
  icon: 'truck',
  is_active: true,
})

export default {
  name: "SectionItems",

  components: { LinkEditor },

  emits: ['changed'],

  props: {
    section: { type: Object, required: true },
    categories: { type: Array, default: () => [] },
    brands: { type: Array, default: () => [] },
  },

  data() {
    return {
      form: null,
      saving: false,
      cropper: { open: false, file: null },
      featureIcons: FEATURE_ICONS,
      linkTypes: LINK_TYPES,
    }
  },

  computed: {
    items() {
      return this.section.items || []
    },
    isFeatures() {
      return this.section.type === 'features'
    },
    singular() {
      return {
        hero_carousel: this.form?.placement === 'side' ? 'side banner' : 'slide',
        banner: 'banner',
        banner_grid: 'tile',
        features: 'feature',
      }[this.section.type] || 'item'
    },
    itemsLabel() {
      return { hero_carousel: 'Slides & side banners', banner: 'Banner images', banner_grid: 'Tiles', features: 'Selling points' }[this.section.type]
    },
    itemsHint() {
      return {
        hero_carousel: 'Main slides rotate. Up to 2 side banners are shown next to the carousel when enabled.',
        banner: 'Several images will rotate automatically.',
        banner_grid: 'Tiles are shown side by side in the number of columns you choose.',
        features: 'Short selling points with an icon.',
      }[this.section.type]
    },
    cropPreset() {
      const presets = ITEM_CROP[this.section.type] || ITEM_CROP.banner
      return presets[this.form?.placement || 'main'] || presets.main
    },
    groups() {
      if (this.section.type !== 'hero_carousel') {
        return [{ placement: 'main', label: '', items: this.items }]
      }
      const groups = [{ placement: 'main', label: 'Main slides', items: this.items.filter(i => i.placement !== 'side') }]
      if (this.section.settings?.show_side_banners) {
        groups.push({ placement: 'side', label: 'Side banners (max 2 shown)', items: this.items.filter(i => i.placement === 'side') })
      }
      return groups
    },
    canSave() {
      if (!this.form) return false
      if (!this.isFeatures && !this.form.image) return false
      if (this.form.link_type !== 'none' && this.form.link_type !== 'search' && !this.form.link_value) return false
      if (this.form.link_type === 'url' && !/^(https?:\/\/[^\s<>"']+|\/[^\s<>"']*)$/i.test(this.form.link_value)) return false
      return true
    },
  },

  methods: {
    startAdd(placement) {
      this.form = emptyItem(placement)
    },
    startEdit(item) {
      this.form = { ...emptyItem(item.placement), ...item, link_value: item.link_value || '', text_color: item.text_color || '' }
    },
    onFileSelected(event) {
      const [file] = event.target.files || []
      if (file) {
        this.cropper.file = file
        this.cropper.open = true
      }
      event.target.value = ''
    },
    payload() {
      const f = this.form
      return {
        placement: f.placement,
        image: f.image || null,
        title: f.title || null,
        subtitle: f.subtitle || null,
        button_text: f.button_text || null,
        link_type: f.link_type || 'none',
        link_value: f.link_type === 'none' ? null : (f.link_value || null),
        text_color: f.text_color || null,
        text_position: f.text_position || 'left',
        icon: this.isFeatures ? f.icon : null,
        is_active: !!f.is_active,
      }
    },
    async save() {
      this.saving = true
      try {
        const result = this.form.id
          ? await storefrontService.updateItem(this.form.id, this.payload())
          : await storefrontService.createItem(this.section.id, this.payload())
        if (result) {
          this.form = null
          this.$emit('changed')
        }
      } finally {
        this.saving = false
      }
    },
    async toggle(item) {
      if (await storefrontService.updateItem(item.id, { is_active: !item.is_active }, null)) this.$emit('changed')
    },
    remove(item) {
      confirmDialog('Item', async () => {
        if (await storefrontService.deleteItem(item.id)) this.$emit('changed')
      }, `Remove "${item.title || 'this item'}"?`)
    },
    async move(group, index, delta) {
      const list = [...group.items]
      const [moved] = list.splice(index, 1)
      list.splice(index + delta, 0, moved)
      const others = this.items.filter(i => !list.includes(i))
      const order = (group.placement === 'side' ? [...others, ...list] : [...list, ...others]).map(i => i.id)
      if (await storefrontService.reorderItems(this.section.id, order)) this.$emit('changed')
    },
  },
}
</script>

<style scoped>
.item-thumb {
  width: 96px;
  height: 40px;
  object-fit: cover;
  background: #f1f3f5;
}

.feature-icon {
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
}

.item-image-preview {
  width: 100%;
  background: #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.item-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.icon-choice {
  width: 36px;
}

.min-w-0 {
  min-width: 0;
}
</style>
